import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib';
import { ADMIN_USER_QUERY_KEYS } from '@/constants';
import {
  type AccessLevel,
  type PermissionKey,
  type SectionKey,
  resolveEffectivePermissions,
  SECTION_TO_PERMISSION,
} from '@/types/permissions';
import { useAuth } from './useAuth';

interface UserPermissions {
  accessLevel: AccessLevel | null;
  /**
   * True when the current admin holds `key`: either their access_level holds
   * it by default (registry) or they have a per-user grant for it
   * (admin_permission_grants), or it follows from one they hold (holding a
   * Reports tab implies section.reports). Grants only ever add.
   */
  can: (key: PermissionKey) => boolean;
  /** Section-level view of `can`, for nav and route guards. */
  hasAccessTo: (section: SectionKey) => boolean;
  /**
   * True when the current admin's tier is `level` or higher on the
   * staff < manager < super_admin ladder. Use this instead of comparing
   * `accessLevel` strings inline so "manager or above" is spelled one way.
   */
  isAtLeast: (level: AccessLevel) => boolean;
  isSuperAdmin: boolean;
  /** Per-user grants on top of the role (empty for most admins). */
  grants: PermissionKey[];
  isLoading: boolean;
}

const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = {
  staff: 0,
  manager: 1,
  super_admin: 2,
};

interface CurrentPermissions {
  accessLevel: AccessLevel;
  grants: PermissionKey[];
  /**
   * True when the grants read failed. The role defaults still apply (a
   * failure can only narrow access, never widen it) and the query polls
   * until the read succeeds instead of caching the gap for five minutes.
   */
  grantsFailed: boolean;
}

const NO_GRANTS: PermissionKey[] = [];
const GRANTS_RETRY_INTERVAL_MS = 15_000;

async function fetchCurrentPermissions(userId: string): Promise<CurrentPermissions | null> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('access_level')
    .eq('id', userId)
    .single();

  if (error || !data) {
    return null;
  }

  let grants: PermissionKey[] = [];
  let grantsFailed = false;
  try {
    const result = await supabase
      .from('admin_permission_grants')
      .select('permission_key')
      .eq('admin_user_id', userId);
    if (result?.error) {
      grantsFailed = true;
    } else {
      const rows = (result?.data ?? []) as { permission_key: string }[];
      grants = rows.map((r) => r.permission_key as PermissionKey);
    }
  } catch {
    grantsFailed = true;
  }

  return { accessLevel: data.access_level as AccessLevel, grants, grantsFailed };
}

export function usePermissions(): UserPermissions {
  // useAuth is the session source; the user id is part of the query key so a
  // PIN fast-switch to another admin never reads the previous admin's cache.
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const { data, isLoading } = useQuery({
    queryKey: [...ADMIN_USER_QUERY_KEYS.CURRENT_PERMISSIONS, userId],
    queryFn: () => fetchCurrentPermissions(userId as string),
    enabled: Boolean(userId),
    // Revokes are pushed via useAdminGrantsRealtime (mounted in Layout), so
    // the cache can stay warm between events.
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => (query.state.data?.grantsFailed ? GRANTS_RETRY_INTERVAL_MS : false),
  });

  const accessLevel = data?.accessLevel ?? null;
  const grants = data?.grants ?? NO_GRANTS;

  const effective = useMemo(() => {
    if (!accessLevel) return new Set<PermissionKey>();
    return resolveEffectivePermissions(accessLevel, grants);
  }, [accessLevel, grants]);

  const can = (key: PermissionKey) => effective.has(key);

  return {
    accessLevel,
    can,
    hasAccessTo: (section: SectionKey) => can(SECTION_TO_PERMISSION[section]),
    isAtLeast: (level: AccessLevel) => {
      if (!accessLevel) return false;
      return ACCESS_LEVEL_RANK[accessLevel] >= ACCESS_LEVEL_RANK[level];
    },
    isSuperAdmin: accessLevel === 'super_admin',
    grants,
    isLoading,
  };
}
